import React from "react";
import PropTypes from "prop-types";
import moment from "moment";

const expiredCertificationsForShift = (certifications, shiftStartTime) =>
  (certifications || []).filter(
    certification =>
      certification.expiration_date &&
      moment(certification.expiration_date).isSameOrBefore(
        moment(shiftStartTime),
        "day"
      )
  )
  .sort(
    (a, b) =>
      moment(a.expiration_date).valueOf() -
      moment(b.expiration_date).valueOf()
  );

const certificationName = certification =>
  (certification.certification_type && certification.certification_type.name) ||
  "Certification";

const certificationWarningText = (certification, userName) => {
  const owner = userName ? `${userName}'s ` : "";
  const tense = moment(certification.expiration_date).isBefore(moment(), "day")
    ? "expired"
    : "expires";
  const date = moment(certification.expiration_date).format("M/D/YYYY");

  return `${owner}${certificationName(certification)} ${tense} on ${date}`;
};

const HoursHeader = ({ startOfWeek, endOfWeek, myHoursThisWeek }) => (
  <p className="grey-text">
    My hours the week of&nbsp;
    {[startOfWeek.format("MMM Do"), endOfWeek.format("MMM Do")].join(" - ")}:&nbsp;
    <br className="visible-xs" />
    {myHoursThisWeek} hours
  </p>
);

const ConflictSection = ({ conflict, conflictSubject, conflictShiftTimes }) =>
  conflict ? (
    <p className="pink-text">
      Conflicts:
      <br />
      <span>
        {conflictSubject} already scheduled to work from {conflictShiftTimes}
      </span>
    </p>
  ) : null;

const HoursSection = ({
  overMaxWeeklyHours,
  overMaxDailyHours,
  maxWeeklyHours,
  maxDailyHours
}) => {
  if (!overMaxWeeklyHours && !overMaxDailyHours) return null;

  return (
    <p className="pink-text">
      Warnings:
      <br />
      {overMaxWeeklyHours ? (
        <span>{maxWeeklyHours} max weekly hours exceeded</span>
      ) : null}
      {overMaxDailyHours ? (
        <span>
          {overMaxWeeklyHours && <br />}
          {maxDailyHours} max daily hours exceeded
        </span>
      ) : null}
    </p>
  );
};

const CertificationSection = ({
  show,
  certifications,
  shiftStartTime,
  userId,
  userName
}) => {
  if (!show || !window.Application.currentAccount.certifications_toggle)
    return null;

  const expired = expiredCertificationsForShift(certifications, shiftStartTime);
  if (expired.length === 0) return null;

  return (
    <p className="pink-text">
      Certification Expiration:
      <br />
      {expired.map((certification, index) => (
        <span key={certification.id}>
          {index > 0 && <br />}
          {certificationWarningText(certification, userName)}
          {userId ? (
            <a
              href={`/users/${userId}#certifications`}
              className="styled-link"
              target="_blank"
              style={{ marginLeft: "4px" }}
            >
              <i className="material-icons md-15 icon-for-dropdown">
                open_in_new
              </i>
            </a>
          ) : null}
        </span>
      ))}
    </p>
  );
};

const ScheduleWarnings = ({
  show,
  hoursHeader,
  conflict,
  conflictSubject,
  conflictShiftTimes,
  overMaxWeeklyHours,
  overMaxDailyHours,
  maxWeeklyHours,
  maxDailyHours,
  certificationVisible,
  certifications,
  shiftStartTime,
  userId,
  userName
}) => {
  if (!show) return null;

  return (
    <div>
      {hoursHeader ? <HoursHeader {...hoursHeader} /> : null}
      <ConflictSection
        conflict={conflict}
        conflictSubject={conflictSubject}
        conflictShiftTimes={conflictShiftTimes}
      />
      <HoursSection
        overMaxWeeklyHours={overMaxWeeklyHours}
        overMaxDailyHours={overMaxDailyHours}
        maxWeeklyHours={maxWeeklyHours}
        maxDailyHours={maxDailyHours}
      />
      <CertificationSection
        show={certificationVisible}
        certifications={certifications}
        shiftStartTime={shiftStartTime}
        userId={userId}
        userName={userName}
      />
    </div>
  );
};

ScheduleWarnings.propTypes = {
  show: PropTypes.bool,
  hoursHeader: PropTypes.shape({
    startOfWeek: PropTypes.object,
    endOfWeek: PropTypes.object,
    myHoursThisWeek: PropTypes.number
  }),
  conflict: PropTypes.bool,
  conflictSubject: PropTypes.string,
  conflictShiftTimes: PropTypes.string,
  overMaxWeeklyHours: PropTypes.bool,
  overMaxDailyHours: PropTypes.bool,
  maxWeeklyHours: PropTypes.number,
  maxDailyHours: PropTypes.number,
  certificationVisible: PropTypes.bool,
  certifications: PropTypes.array,
  shiftStartTime: PropTypes.string,
  userId: PropTypes.number,
  userName: PropTypes.string
};

ScheduleWarnings.defaultProps = {
  show: false,
  conflictSubject: "Employee is"
};

export default ScheduleWarnings;
